/**
 * Comportamientos de la ficha de producto que no se pueden resolver con CSS.
 *
 * Los tabs son CSS-only (`input[type=radio]` + `:checked ~`), asi que aca no hay nada de eso.
 * Cerrar el modal tampoco: lo hacen el `<form method="dialog">` y el `closedby` del snippet.
 */

/**
 * Abre un `<dialog>` como modal desde cualquier boton con `data-dialog-open="<id>"`.
 * Delegado en `document`, asi funciona con marcado que llega por AJAX (quickshop, recomendados).
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-dialog-open]');
  if (!trigger) return;

  const dialog = document.getElementById(trigger.dataset.dialogOpen);
  if (dialog instanceof HTMLDialogElement) dialog.showModal();
});

/**
 * Enter y Espacio sobre un `[role="button"]` que no es un `<button>`.
 *
 * Las miniaturas de la galeria son `<div>` porque Flickity las posiciona como celdas y un
 * `<button>` traeria su propio estilo a pelear con eso. Un div con rol de boton no activa
 * solo: el navegador solo hace eso con los controles nativos. Sin esto la fila de
 * miniaturas es una hilera de tab stops que no responden a ninguna tecla.
 *
 * Espacio ademas necesita `preventDefault` para que la pagina no se desplace.
 */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const boton = event.target.closest('[role="button"]');
  if (!boton || boton.matches('button, a, input')) return;

  event.preventDefault();
  boton.click();
});

/**
 * Fallback de `closedby="any"` para Safari, que todavia no lo soporta.
 *
 * Un click en el backdrop llega con el propio `<dialog>` como target, asi que hay que
 * comparar las coordenadas contra su caja para distinguirlo de un click en su padding.
 */
if (!('closedBy' in HTMLDialogElement.prototype)) {
  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const caja = dialog.getBoundingClientRect();
    const adentro =
      event.clientY >= caja.top &&
      event.clientY <= caja.bottom &&
      event.clientX >= caja.left &&
      event.clientX <= caja.right;

    if (!adentro) dialog.close();
  });
}

/**
 * Lleva al bloque de resenas con desplazamiento suave en vez del salto del navegador.
 *
 * Se hace por JS y no con `scroll-behavior: smooth` en `html` porque eso volveria suave
 * todo salto de ancla del sitio, incluido el que dispara la paginacion de las resenas.
 * El offset contra el header lo pone el `scroll-margin-top` de pdp.css.
 */
document.addEventListener('click', (event) => {
  const enlace = event.target.closest('a.product-rating');
  if (!enlace) return;

  const destino = document.querySelector(enlace.hash);
  if (!destino) return;

  event.preventDefault();
  const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  destino.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' });
  // El hash no lo escribe el navegador al cancelar el evento; sin el, la URL no
  // queda compartible ni el boton de atras vuelve al punto anterior.
  history.pushState(null, '', enlace.hash);
});

/** Desborde que todavia cuenta como "entra": redondeo de subpixel. */
const UMBRAL_TABS = 2;

/**
 * Marca de que lado de la fila de pestañas quedo contenido fuera de vista.
 *
 * Escribe `data-tabs-overflow` con 'left', 'right' o 'both'; sin desborde borra el
 * atributo. pdp.css lo traduce a la mascara de difuminado. Mismo vocabulario que
 * `swatchScroll` de sections.js, que hace lo propio con la fila de swatches.
 *
 * @param {Element} strip - El contenedor con `data-tabs-scroller`.
 */
function marcarDesbordeTabs(strip) {
  const sobra = strip.scrollWidth - strip.clientWidth;
  if (sobra <= UMBRAL_TABS) {
    strip.removeAttribute('data-tabs-overflow');
    return;
  }

  const inicio = strip.scrollLeft <= 1;
  const fin = strip.scrollLeft >= sobra - 1;
  let lado = 'both';
  if (inicio) lado = 'right';
  else if (fin) lado = 'left';
  strip.setAttribute('data-tabs-overflow', lado);
}

for (const strip of document.querySelectorAll('[data-tabs-scroller]')) {
  marcarDesbordeTabs(strip);
  strip.addEventListener('scroll', () => marcarDesbordeTabs(strip), { passive: true });
  // El observer cubre el cambio de ancho de la ventana; la fuente que llega tarde
  // cambia el ancho del contenido pero no el del contenedor, y no lo dispara.
  new ResizeObserver(() => marcarDesbordeTabs(strip)).observe(strip);
  document.fonts?.ready.then(() => marcarDesbordeTabs(strip));
}

/** Pixeles de movimiento a partir de los cuales el gesto es un arrastre y no un click. */
const UMBRAL_ARRASTRE = 4;

/** El arrastre en curso, o null. */
let arrastreTabs = null;

/** La fila que se acaba de arrastrar, para descartar su `click` de cierre. */
let tabsArrastradas = null;

/**
 * Cierra el arrastre y, si hubo movimiento, marca la fila para que el `click`
 * que llega despues no cambie de pestaña.
 *
 * El `click` se dispara sobre el label donde se solto el boton, no sobre el que
 * inicio el gesto: sin descartarlo, arrastrar de una pestaña a otra la activaria.
 */
function terminarArrastreTabs() {
  if (!arrastreTabs) return;

  const { strip, arrastro } = arrastreTabs;
  arrastreTabs = null;
  strip.classList.remove('is-dragging');
  if (!arrastro) return;

  tabsArrastradas = strip;
  setTimeout(() => {
    tabsArrastradas = null;
  }, 0);
}

/** La fila de pestañas que contiene al objetivo del evento, si hay alguna. */
function stripDe(objetivo) {
  if (!(objetivo instanceof Element)) return null;
  return objetivo.closest('[data-tabs-scroller]');
}

/**
 * Arrastre con el mouse de la fila de pestañas.
 *
 * La barra de desplazamiento va oculta, asi que en escritorio el contenido de mas
 * solo se alcanzaba con la rueda horizontal, que casi nadie tiene. Mismo patron que
 * `swatchScroll` de sections.js: se sigue en `document` para que el gesto sobreviva
 * al puntero saliendo de la fila. El tactil ya desplaza solo y no pasa por aca.
 */
document.addEventListener('pointerdown', (evento) => {
  if (evento.pointerType !== 'mouse' || evento.button !== 0) return;

  const strip = stripDe(evento.target);
  if (!strip || strip.scrollWidth - strip.clientWidth <= UMBRAL_TABS) return;

  arrastreTabs = {
    strip,
    puntero: evento.pointerId,
    x: evento.clientX,
    inicio: strip.scrollLeft,
    arrastro: false,
  };
});

document.addEventListener('pointermove', (evento) => {
  if (!arrastreTabs || evento.pointerId !== arrastreTabs.puntero) return;

  // El boton pudo soltarse fuera de la ventana, donde no llega ningun `pointerup`.
  if (evento.buttons === 0) {
    terminarArrastreTabs();
    return;
  }

  const avance = evento.clientX - arrastreTabs.x;
  if (!arrastreTabs.arrastro) {
    if (Math.abs(avance) < UMBRAL_ARRASTRE) return;
    arrastreTabs.arrastro = true;
    arrastreTabs.strip.classList.add('is-dragging');
  }

  arrastreTabs.strip.scrollLeft = arrastreTabs.inicio - avance;
  // Sin esto el gesto arranca el arrastre nativo del texto del label.
  evento.preventDefault();
});

document.addEventListener('pointerup', terminarArrastreTabs);
document.addEventListener('pointercancel', terminarArrastreTabs);

document.addEventListener(
  'click',
  (evento) => {
    if (!tabsArrastradas || stripDe(evento.target) !== tabsArrastradas) return;
    evento.stopPropagation();
    evento.preventDefault();
  },
  // En captura, antes de que el label llegue a activar su radio.
  true,
);

const MARGEN_TOOLTIP = 8;

/**
 * Empuja el tooltip de un atributo hacia adentro cuando se sale de la pantalla.
 *
 * El tooltip va centrado sobre un icono de 70 px, asi que el de un icono pegado al borde
 * desborda y produce scroll horizontal en toda la pagina. Escribe `--desvio` en px; el CSS lo
 * suma al `translate` del tooltip y lo resta al de la punta, que asi se queda sobre el icono.
 * Sin este script `--desvio` vale 0 y el tooltip queda centrado.
 *
 * @param {Element} item - El `<li>` del atributo.
 */
function ajustarTooltip(item) {
  const tip = item.querySelector('.product-attributes__tip');
  const icono = item.querySelector('.product-attributes__trigger');
  if (!tip || !icono) return;

  // La posicion se deduce del icono y no se lee del tooltip: `transform` tiene una transicion
  // de 150 ms, asi que `getBoundingClientRect()` sobre el tooltip devuelve el valor en vuelo y
  // el desvio sale mal. `offsetWidth` e icono no dependen del transform.
  const caja = icono.getBoundingClientRect();
  const centro = caja.left + caja.width / 2;
  const mitad = tip.offsetWidth / 2;
  const limite = document.documentElement.clientWidth - MARGEN_TOOLTIP;

  let desvio = 0;
  if (centro + mitad > limite) desvio = limite - (centro + mitad);
  if (centro - mitad + desvio < MARGEN_TOOLTIP) desvio = MARGEN_TOOLTIP - (centro - mitad);

  tip.style.setProperty('--desvio', `${Math.round(desvio)}px`);
}

/**
 * El desvio se calcula recien al abrir y no en reposo ni en cada `resize`: cerrado el tooltip
 * va con `display: none`, asi que no ocupa lugar y no puede generar scroll horizontal por su
 * cuenta. Ajustarlo mientras se arrastra la ventana solo hacia aparecer y desaparecer la barra.
 */
for (const evento of ['pointerenter', 'focusin']) {
  document.addEventListener(
    evento,
    (event) => {
      const item = event.target instanceof Element && event.target.closest('.product-attributes__item');
      if (item) ajustarTooltip(item);
    },
    // `pointerenter` no burbujea: hay que escucharlo en fase de captura.
    true,
  );
}

/** Cuanto suma el checkbox de merma sobre el area del proyecto. */
const MERMA = 0.1;

/**
 * Tolerancia del cociente antes de redondear para arriba.
 *
 * `45.6 / 2.28` da `20.000000000000004` en punto flotante, asi que sin esto una
 * superficie que entra en 20 cajas justas cotizaba 21. Un mm² de holgura no cambia
 * ninguna cuenta real. El theme viejo tiene este bug.
 */
const TOLERANCIA_CAJAS = 1e-9;

/**
 * Recalcula una calculadora de superficie y le escribe las cajas al contador de compra
 * de su propia ficha.
 *
 * Con el area vacia o en cero limpia el resultado y **no** toca el contador: borrar el
 * campo no es lo mismo que pedir cero cajas.
 *
 * @param {Element} calc - El `[data-m2-calc]` a recalcular.
 */
function calcularCajas(calc) {
  const areaInput = calc.querySelector('[data-m2-area]');
  const resultado = calc.querySelector('[data-m2-result]');
  const porCaja = Number(calc.dataset.areaPerBox);
  // El teclado latino da la coma decimal; `Number` la leeria como NaN.
  const area = Number(String(areaInput.value).replace(',', '.'));

  if (!(area > 0) || !(porCaja > 0)) {
    resultado.textContent = '';
    return;
  }

  const aCubrir = calc.querySelector('[data-m2-waste]').checked ? area * (1 + MERMA) : area;
  const cajas = Math.ceil(aCubrir / porCaja - TOLERANCIA_CAJAS);

  const plantilla = cajas === 1 ? calc.dataset.resultOne : calc.dataset.resultMany;
  resultado.textContent = plantilla
    .replace('%boxes%', cajas)
    // `value` de un `type="number"` siempre sale con punto decimal, tambien cuando
    // se tecleo la coma: sin esto el resultado dice "45.6 m²" en una tienda que
    // escribe "45,6" en todos los demas numeros de la ficha.
    .replace('%area%', area.toLocaleString(document.documentElement.lang || undefined))
    .replace('%unit%', calc.dataset.unit);

  const contador = calc.closest('.product__details')?.querySelector('input.quantity');
  if (contador) {
    contador.value = cajas;
    // El theme lleva su propio estado del contador y lo sincroniza con este evento.
    contador.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * Un solo handler para el campo y para el checkbox: los dos disparan `input`.
 *
 * Que sea uno es el arreglo del bug del theme viejo, donde el campo y el checkbox
 * tenian cada uno su funcion y escribir el area despues de marcar la merma perdia el
 * 10% sin desmarcar nada.
 */
document.addEventListener('input', (evento) => {
  const calc = evento.target instanceof Element && evento.target.closest('[data-m2-calc]');
  if (calc) calcularCajas(calc);
});

/**
 * Nombre accesible para el `<select>` de orden que inyecta la app de resenas.
 *
 * Revie lo crea por JS sin `aria-label` ni `<label>`, asi que en el arbol de
 * accesibilidad queda como un combo sin nombre. Es el unico audit que falla en la
 * categoria de agentes. El texto va literal: la app rotula sus propias opciones en
 * espanol y no pasa por los locales del theme.
 */
const contenedorResenas = document.getElementById('cont-rl-w');
if (contenedorResenas) {
  const nombrarOrdenResenas = () => {
    const orden = contenedorResenas.querySelector('select.rvp-sel:not([aria-label])');
    if (orden) orden.setAttribute('aria-label', 'Ordenar reseñas');
  };
  // Sigue vivo: la app rehace la lista al cambiar el orden, y con ella el `<select>`.
  new MutationObserver(nombrarOrdenResenas).observe(contenedorResenas, { childList: true, subtree: true });
  nombrarOrdenResenas();
}

/**
 * Publica el alto de la galeria en `--pdp-gallery-h`, que pdp.css usa para anclar el sticky.
 *
 * No hay CSS que lo resuelva solo: `bottom` en un sticky es un tope contra bajar, no un
 * ancla al fondo, y el alto depende del ancho de la columna porque la foto es cuadrada.
 */
const galeriaPdp = document.querySelector('.product_section > .product__images');

if (galeriaPdp) {
  new ResizeObserver(([entrada]) => {
    galeriaPdp.style.setProperty('--pdp-gallery-h', `${entrada.borderBoxSize[0].blockSize}px`);
  }).observe(galeriaPdp);
}

/**
 * Barra fija de compra en movil: aparece mientras el boton original no esta en pantalla.
 *
 * No tiene formulario propio, maneja el original. El alta es el `.click()` del boton de
 * verdad, asi que hereda el AJAX, el bloqueo durante el envio y el aviso al lector de
 * pantalla. El contador pasa por los `.js-change-quantity` del theme, que respetan `min`
 * y `max`.
 *
 * @param {ParentNode} raiz - El documento, o la seccion que el editor acaba de recargar.
 */
function iniciarBarraCompra(raiz) {
  for (const barra of raiz.querySelectorAll('[data-sticky-cart]')) {
    const ficha = barra.closest('[data-product-form]');
    const original = ficha.querySelector('form .add_to_cart');
    const cantidad = ficha.querySelector('form input.quantity');
    const contador = barra.querySelector('[data-sticky-cart-count]');
    const reflejar = () => {
      if (cantidad && contador) contador.value = cantidad.value;
    };

    barra.querySelector('[data-sticky-cart-add]').addEventListener('click', () => original.click());

    for (const paso of barra.querySelectorAll('[data-sticky-cart-step]')) {
      paso.addEventListener('click', () => {
        ficha.querySelector(`form .js-change-quantity[data-func="${paso.dataset.stickyCartStep}"]`).click();
      });
    }

    // Con jQuery y no `addEventListener`: los +/- del theme avisan con `$.trigger('change')`,
    // que un listener nativo no recibe. El de jQuery recibe ese y tambien el `change` nativo
    // de la calculadora de cajas.
    if (cantidad) window.jQuery(cantidad).on('change input', reflejar);
    reflejar();

    // Los margenes descuentan el header fijo y la propia barra: el boton tapado por
    // cualquiera de los dos no esta a la vista aunque toque el viewport.
    const arriba = document.getElementById('header')?.offsetHeight ?? 0;
    new IntersectionObserver(
      ([entrada]) => barra.toggleAttribute('data-visible', !entrada.isIntersecting),
      { rootMargin: `-${arriba}px 0px -${barra.offsetHeight}px 0px` }
    ).observe(original);
  }
}

iniciarBarraCompra(document);
document.addEventListener('shopify:section:load', (evento) => iniciarBarraCompra(evento.target));
